// Main application entry point
import type { AppContext, AppConfig, DeviceInfo } from "./types/index.ts";
import { createEventBus } from "./core/eventBus.ts";
import { detectDeviceInfo } from "./utils/device.ts";
import { createRouter } from "./router.ts";
import { initializeIconRegistry } from "./components/UI/Icon/index.ts";
import { createSessionManager } from "./data/session.ts";

// Import test helpers dropdown for development

/**
 * Creates the main application
 * @param container - Root container element
 * @param config - Application configuration
 * @returns Application API with destroy method
 */
const createApp = async (container: HTMLElement, config: AppConfig = {}) => {
  console.log("🚀 Initializing Unnamed App - Sketch to Code");

  // Initialize icon registry first
  console.log("🎨 Loading icons...");
  await initializeIconRegistry();
  console.log("✅ Icons loaded successfully");

  // Create event bus for component communication
  const eventBus = createEventBus();

  // Create app context
  const appContext: AppContext = {
    container,
    eventBus,
    config: {
      canvasWidth: 800,
      canvasHeight: 600,
      tools: ["ballpoint", "highlighter", "eraser"],
      debug: true,
      ...config,
    },
  };

  // Session manager
  const sessionManager = createSessionManager(appContext);
  (appContext as any).session = sessionManager;

  // Initialize device info display (will be handled by router)
  const deviceInfo = detectDeviceInfo();

  // Log initialization for debugging
  if (appContext.config.debug) {
    console.log("📱 Device Info:", deviceInfo);
    console.log("⚙️ App Config:", appContext.config);
  }

  // Make device info available globally for routes to access BEFORE router renders
  (appContext as any).deviceInfo = deviceInfo;

  // Decide initial route based on auth BEFORE initializing router to avoid flash
  try {
    const currentPath = window.location.pathname || "/";
    const { getMe } = await import("./api");
    await getMe();
    await sessionManager.refresh();

    // If already on auth or root, go to stage; otherwise keep the route
    if (currentPath === "/auth" || currentPath === "/") {
      window.history.replaceState({}, "", "/stage");
    }
  } catch {
    // Not authenticated → route to /auth from any route
    try {
      const currentPath = window.location.pathname || "/";
      if (currentPath !== "/auth") {
        window.history.replaceState({}, "", "/auth");
      }
    } catch {}
  }

  // Initialize router
  const router = createRouter(container, appContext);

  // Return app API
  return {
    getContext: () => appContext,
    getRouter: () => router,
    destroy: () => {
      router.destroy();
      eventBus.destroy();
      sessionManager.destroy();
      console.log("🧹 App destroyed");
    },
  };
};

/**
 * Display device information in the header
 * This function is now available for routes to use
 */
export const displayDeviceInfo = (info: DeviceInfo): void => {
  const deviceInfoElement = document.getElementById("device-info");
  if (deviceInfoElement) {
    deviceInfoElement.textContent = `${info.platform} | ${info.screenWidth}×${
      info.screenHeight
    } | ${info.pixelRatio}x | Touch: ${info.touchSupport ? "Yes" : "No"}`;
  }
};

// Initialize app when DOM is ready
document.addEventListener("DOMContentLoaded", async () => {
  const appContainer = document.getElementById("app");
  if (!appContainer) {
    throw new Error("App container not found");
  }

  // Create and mount the application with router
  const app = await createApp(appContainer, {
    debug: true,
  });

  // If already authenticated, route to stage unless the user is explicitly on auth
  // (Handled pre-router to avoid visual flash)

  // Make app globally available for debugging
  if (typeof window !== "undefined") {
    (window as any).app = app;
  }
});

export { createApp };
